import React, { useState, useEffect } from 'react';
import { Modal, Input, InputNumber, DatePicker, Button, Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { CalendarOutlined, SwapRightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { postDataRequest } from '../../api/serviceMethods';
import { EVENT_CODE_BATCHES_URL } from '../../api/URLs';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const CreateInviteBatchModal = ({
  open,
  onClose,
  onSuccess,
  eventId,
  eventTitle,
  eventStartDate,
  eventEndDate,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const defaultStart = eventStartDate ? dayjs(eventStartDate) : dayjs();
      const defaultEnd = eventEndDate
        ? dayjs(eventEndDate)
        : dayjs().add(2, 'month').endOf('day');

      form.setFieldsValue({
        totalQuantity: undefined,
        validityRange: [defaultStart, defaultEnd],
        remarks: '',
      });
    }
  }, [open, eventStartDate, eventEndDate, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const [start, end] = values.validityRange || [];
      const defaultStart = eventStartDate ? dayjs(eventStartDate) : dayjs();
      const defaultEnd = eventEndDate
        ? dayjs(eventEndDate)
        : dayjs().add(2, 'month').endOf('day');

      const finalStart = start || defaultStart;
      const finalEnd = end || defaultEnd;

      const payload = {
        eventId: eventId || '',
        totalQuantity: Number(values.totalQuantity),
        discountValue: 0,
        discountType: 'event_discount',
        validityType: 'date_range',
        validFrom: finalStart.startOf('day').toISOString(),
        validTo: finalEnd.endOf('day').toISOString(),
        remarks: values.remarks ? values.remarks.trim() : '',
      };

      const res = await postDataRequest(EVENT_CODE_BATCHES_URL, payload);
      setLoading(false);

      if (res && (res.status === 200 || res.status === 201 || res.data)) {
        message.success(t('Created successfully!'));
        form.setFieldsValue({
          totalQuantity: undefined,
          remarks: '',
        });
        form.resetFields();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        message.warning((res && res.message) || t('An error occurred.'));
      }
    } catch (err) {
      setLoading(false);
      // Ant Design form validation failure: inline error is already shown, do NOT show "Server Error!"
      if (err?.errorFields) {
        return;
      }
      console.error('Create invite batch error:', err);
      const errMsg =
        (typeof err === 'string' && err) ||
        err?.message ||
        err?.error ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t('Server Error!') ||
        'Server Error!';
      message.error(errMsg);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
          {t('Create Invite Number') || '创建邀请码'}
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '4px 0 8px' }}>
          <Button
            key="submit"
            type="primary"
            danger
            size="large"
            loading={loading}
            onClick={handleSubmit}
            style={{
              backgroundColor: '#E50A47',
              borderColor: '#E50A47',
              borderRadius: 6,
              minWidth: 160,
              height: 40,
              fontSize: 15,
              fontWeight: 500,
            }}
          >
            {t('Submit') || '提交'}
          </Button>
        </div>
      }
      destroyOnClose
      centered
      width={520}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* {eventTitle && (
          <div
            style={{
              marginBottom: 18,
              padding: '10px 14px',
              backgroundColor: '#f8f9fa',
              borderRadius: 6,
              fontSize: 13,
              color: '#595959',
              border: '1px solid #ebebeb',
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1.5,
            }}
          >
            <span style={{ fontWeight: 600, color: '#262626', marginRight: 6 }}>
              {t('Event') || '活动'}:
            </span>
            <span
              style={{
                color: '#262626',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={eventTitle}
            >
              {eventTitle}
            </span>
          </div>
        )} */}

        <Form.Item
          name="totalQuantity"
          label={
            <span style={{ fontWeight: 500, fontSize: 14, color: '#262626' }}>
              <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
              {t('Total Quantity') || '总数量'}
            </span>
          }
          rules={[
            { required: true, message: '' },
            {
              type: 'number',
              min: 1,
              message: t('Quantity must be greater than 0'),
            },
          ]}
          required={false}
        >
          <InputNumber
            size="large"
            style={{ width: '100%', borderRadius: 6 }}
            placeholder={t('')}
            min={1}
            precision={0}
          />
        </Form.Item>

        {/* <Form.Item
          name="validityRange"
          label={
            <span style={{ fontWeight: 500, fontSize: 14, color: '#262626' }}>
              <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
              {t('Validity Period') || '有效期'}
            </span>
          }
          rules={[
            { required: true, message: t('Please select validity period') || '请选择有效期' },
          ]}
          required={false}
        >
          <RangePicker
            size="large"
            style={{ width: '100%', borderRadius: 6 }}
            format="YYYY-MM-DD"
            placeholder={[t('Start Date') || '开始日期', t('End Date') || '结束日期']}
            separator={<SwapRightOutlined style={{ color: '#8c8c8c' }} />}
            suffixIcon={<CalendarOutlined style={{ color: '#E50A47' }} />}
          />
        </Form.Item> */}

        <Form.Item
          name="remarks"
          label={
            <span style={{ fontWeight: 500, fontSize: 14, color: '#262626' }}>
              {t('Batch Remarks')}
            </span>
          }
        >
          <TextArea
            rows={3}
            placeholder={t('')}
            style={{ borderRadius: 6 }}
            maxLength={200}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateInviteBatchModal;
